<template>
  <section class="app-table-wrap expenses-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="expenses-table-sort-btn"
                :title="labels.sortTitles.date"
                @click="toggleSort('date')"
              >
                <span>{{ labels.columns.date }}</span>
                <i class="fa" :class="sortIconClass('date')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="showProviderColumn">
              <button
                type="button"
                class="expenses-table-sort-btn"
                :title="labels.sortTitles.provider"
                @click="toggleSort('provider')"
              >
                <span>{{ labels.columns.provider }}</span>
                <i class="fa" :class="sortIconClass('provider')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.category }}</th>
            <th>{{ labels.columns.concept }}</th>
            <th v-if="showBusinessColumn">{{ labels.columns.business }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="expenses-table-sort-btn expenses-table-sort-btn--right"
                :title="labels.sortTitles.subtotal"
                @click="toggleSort('subtotal')"
              >
                <span>{{ labels.columns.subtotal }}</span>
                <i class="fa" :class="sortIconClass('subtotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.taxes }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="expenses-table-sort-btn expenses-table-sort-btn--right"
                :title="labels.sortTitles.total"
                @click="toggleSort('total')"
              >
                <span>{{ labels.columns.total }}</span>
                <i class="fa" :class="sortIconClass('total')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="expenses-table-sort-btn"
                :title="labels.sortTitles.paymentDate"
                @click="toggleSort('payment_date')"
              >
                <span>{{ labels.columns.paymentDate }}</span>
                <i class="fa" :class="sortIconClass('payment_date')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="splitPaymentAndStatusColumns">{{ labels.columns.status }}</th>
            <th>{{ labels.columns.reference }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columnCount" class="app-table-empty">{{ loadingText }}</td>
          </tr>

          <tr v-else-if="normalizedRows.length === 0">
            <td :colspan="columnCount" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in sortedRows" :key="row.id">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td>{{ formatDate(row.date) }}</td>
            <td v-if="showProviderColumn" class="app-strong">
              <a
                v-if="row?.provider_id"
                :href="resolveProviderHref(row)"
                class="expenses-table-link"
                  :title="labels.links.providerDetail"
                @click="navigateToProviderDetail(row, $event)"
              >
                {{ row.provider || '' }}
              </a>
              <span v-else>{{ row.provider || '' }}</span>
            </td>
            <td>
              <div class="expenses-cell-stack">
                <strong class="expenses-cell-main">{{ resolveExpenseClassName(row) }}</strong>
                <span class="expenses-cell-sub">{{ resolveCategoryName(row) }}</span>
              </div>
            </td>
            <td>
              <div class="expenses-cell-stack">
                <strong v-if="resolveFolio(row) !== ''" class="expenses-cell-main">{{ resolveFolio(row) }}</strong>
                <span class="expenses-cell-sub" :class="{ 'expenses-cell-sub--with-folio': resolveFolio(row) !== '' }">
                  {{ row.concept || '' }}
                </span>
              </div>
            </td>
            <td v-if="showBusinessColumn">
              <div class="expenses-cell-stack">
                <a
                  v-if="row?.customer_id"
                  :href="resolveCustomerHref(row)"
                  class="expenses-table-link expenses-cell-main"
                  :title="labels.links.customerDetail"
                  @click="navigateToCustomerDetail(row, $event)"
                >
                  {{ resolveCustomerName(row) }}
                </a>
                <span v-else class="expenses-cell-main">{{ resolveCustomerName(row) }}</span>

                <a
                  v-if="row.sale_id"
                  :href="resolveSaleHref(row)"
                  class="expenses-table-link expenses-cell-sub"
                  :title="labels.links.saleDetail"
                  @click="navigateToSaleDetail(row, $event)"
                >
                  {{ resolveSaleDisplayName(row) }}
                </a>
                <span v-else class="expenses-cell-sub">{{ resolveSaleDisplayName(row) }}</span>
              </div>
            </td>
            <td class="app-text-right app-strong">
              <strong class="amount-cell-subtotal">{{ formatAmountWithCurrency(resolveSubtotal(row), row.currency) }}</strong>
            </td>
            <td>
              <div class="expenses-cell-stack" v-if="resolveTaxItems(row).length > 0">
                <small
                  v-for="(taxItem, taxIndex) in resolveTaxItems(row)"
                  :key="`${row.id}-tax-${taxIndex}`"
                  class="amount-cell-tax-line"
                >
                  {{ formatTaxLine(taxItem, row.currency) }}
                </small>
              </div>
              <span v-else></span>
            </td>
            <td class="app-text-right app-strong">
              <div class="amount-cell">
                <span class="amount-cell-total">{{ formatAmountWithCurrency(resolveTotal(row), row.currency) }}</span>
                <small v-if="isForeignCurrency(row.currency)" class="amount-cell-exchange-rate">
                  TC: {{ formatExchangeRate(resolveExchangeRate(row)) }}
                </small>
              </div>
            </td>
            <td v-if="!splitPaymentAndStatusColumns">
              <div class="expenses-cell-stack">
                <span>{{ formatDate(row.payment_date) }}</span>
                <span class="app-status-pill expenses-status-pill" :class="resolveStatusClass(row)">
                  {{ row.status || '' }}
                </span>
              </div>
            </td>
            <td v-else>{{ formatDate(row.payment_date) }}</td>
            <td v-if="splitPaymentAndStatusColumns">
              <span class="app-status-pill expenses-status-pill" :class="resolveStatusClass(row)">
                {{ row.status || '' }}
              </span>
            </td>
            <td>
              <button
                v-if="showReferenceActionButton(row)"
                type="button"
                class="expenses-action-link"
                @click="emitReferenceAction(row)"
              >
                {{ referenceActionLabel(row) }}
              </button>
              <a
                v-else-if="canNavigateToExpenseReceipt(row)"
                :href="resolveExpenseReceiptHref(row)"
                class="expenses-table-link"
                :title="labels.links?.paymentDetail || 'Ir al detalle del pago'"
                @click="navigateToExpenseReceiptDetail(row, $event)"
              >
                {{ row.reference || '' }}
              </a>
              <span v-else>{{ row.reference || '' }}</span>
            </td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  v-for="action in resolveInlineRowActions(row)"
                  :key="`${row.id}-action-${action.key}`"
                  type="button"
                  class="app-table-icon-btn"
                  :class="action.buttonClass"
                  :title="action.title"
                  @click="runInlineRowAction(action, row)"
                >
                  <i class="fa" :class="action.icon" aria-hidden="true"></i>
                </button>

                <div v-if="resolveMenuRowActions(row).length > 0" class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="labels.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow && openRowMenuActions.length > 0"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button
          v-for="action in openRowMenuActions"
          :key="`menu-${openRow.id}-${action.key}`"
          type="button"
          @click="runMenuRowAction(action, openRow)"
        >
          {{ action.title }}
        </button>
      </div>
    </teleport>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { toBooleanFlag } from '@/utils/dataNormalization';
import { formatDateShort } from '@/utils/datePresentation';
import {
  formatAmountWithCurrency,
  formatExchangeRate,
  getRequiredExchangeRate,
  isForeignCurrency,
} from '@/utils/numberFormat';
import { formatTaxLine } from '@/composables/shared/useTaxPresentation';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import {
  resolveSortChange,
  resolveSortIconClass,
  resolveSortTogglePayload,
  sortRows,
} from '@/composables/shared/useSorting';
import {
  canExpenseFeeDelete,
  canExpenseFeeUnapprove,
  hasExpenseFeeRelatedPayment,
  isExpenseFeePaid,
  isExpenseFeePending,
  isExpenseFeeUnapproved,
  resolveExpenseFeeReferenceActionLabel,
  resolveExpenseFeeStatusClass,
  showExpenseFeeReferenceAction,
} from '@/composables/expensesFees/shared/useExpenseFeeTableRowActions';

const MAX_INLINE_ROW_ACTIONS = 3;

export default {
  name: 'ExpenseFeeTable',
  components: {
    AppPagination,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      required: true,
    },
    loadingText: {
      type: String,
      required: true,
    },
    labels: {
      type: Object,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    showBusinessColumn: {
      type: Boolean,
      default: true,
    },
    splitPaymentAndStatusColumns: {
      type: Boolean,
      default: false,
    },
    showProviderColumn: {
      type: Boolean,
      default: true,
    },
    useExternalSort: {
      type: Boolean,
      default: false,
    },
    sortField: {
      type: String,
      default: 'date',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
  },
  emits: [
    'page-change',
    'pay-expense',
    'delete-expense',
    'edit-expense',
    'approve-expense',
    'download-xml',
    'download-pdf',
    'download-receipt',
    'sort-change',
  ],
  data() {
    return {
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
      tableSort: {
        field: 'date',
        direction: 'asc',
      },
    };
  },
  computed: {
    currentSort() {
      if (this.useExternalSort) {
        return {
          field: String(this.sortField || 'date').trim() || 'date',
          direction: String(this.sortDirection || 'asc').trim().toLowerCase() === 'desc' ? 'desc' : 'asc',
        };
      }

      return this.tableSort;
    },
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    sortedRows() {
      if (this.useExternalSort) {
        return this.normalizedRows;
      }

      return sortRows(this.normalizedRows, this.tableSort, {
        date: (row) => new Date(String(row?.date || '')).getTime() || 0,
        provider: (row) => String(row?.provider || '').trim(),
        subtotal: (row) => Number(this.resolveSubtotal(row) || 0),
        total: (row) => Number(this.resolveTotal(row) || 0),
        payment_date: (row) => new Date(String(row?.payment_date || '')).getTime() || 0,
      });
    },
    normalizedPagination() {
      const raw = this.pagination || {};

      return {
        current_page: Number(raw.current_page || 1),
        per_page: Number(raw.per_page || 30),
        total_pages: Number(raw.total_pages || 1),
        total_items: Number(raw.total_items || 0),
      };
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.normalizedRows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    openRowMenuActions() {
      if (!this.openRow) {
        return [];
      }

      return this.resolveMenuRowActions(this.openRow);
    },
    floatingMenuStyle() {
      return {
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
      };
    },
    columnCount() {
      let count = 11;

      if (!this.showProviderColumn) {
        count -= 1;
      }

      if (this.showBusinessColumn) {
        count += 1;
      }

      if (this.splitPaymentAndStatusColumns) {
        count += 1;
      }

      return count;
    },
    sortableFields() {
      const fields = ['date', 'subtotal', 'total', 'payment_date'];
      if (this.showProviderColumn) {
        fields.splice(1, 0, 'provider');
      }

      return fields;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    formatAmountWithCurrency,
    formatExchangeRate,
    isForeignCurrency,
    formatTaxLine,
    sortIconClass(field) {
      return resolveSortIconClass(this.currentSort, field);
    },
    toggleSort(field) {
      const nextSortPayload = resolveSortTogglePayload(
        this.currentSort,
        field,
        this.sortableFields,
      );

      if (!nextSortPayload) {
        return;
      }

      if (this.useExternalSort) {
        this.$emit('sort-change', nextSortPayload);
        return;
      }

      this.tableSort = resolveSortChange(
        this.tableSort,
        nextSortPayload,
        this.sortableFields,
      );
    },
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 30);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      if (nextPage < 1 || nextPage > this.normalizedPagination.total_pages) {
        return;
      }

      if (nextPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', nextPage);
    },
    formatDate(value) {
      const formatted = formatDateShort(value);
      return formatted === '-' ? '' : formatted;
    },
    resolveExpenseClassName(row) {
      return String(row?.expense_class || '').trim() || '';
    },
    resolveCategoryName(row) {
      return String(row?.category || '').trim() || '';
    },
    resolveFolio(row) {
      return String(row?.folio || '').trim() || '';
    },
    resolveCustomerName(row) {
      const customerName = String(row?.customer || '').trim();
      if (customerName !== '') {
        return customerName;
      }

      if (this.resolveSaleDisplayName(row) !== '') {
        return 'Multicliente';
      }

      return '';
    },
    resolveSaleDisplayName(row) {
      const rawSaleName = String(row?.sale_name || '').trim();
      const saleCode = String(row?.sale_code || row?.sale?.code || '').trim();

      if (saleCode === '') {
        return rawSaleName;
      }

      if (rawSaleName === '') {
        return saleCode;
      }

      if (rawSaleName.toUpperCase().startsWith(`${saleCode.toUpperCase()} -`)) {
        return rawSaleName;
      }

      return `${saleCode} - ${rawSaleName}`;
    },
    resolveExchangeRate(row) {
      return getRequiredExchangeRate(row);
    },
    resolveSubtotal(row) {
      const rawSubtotal = Number(row?.subtotal ?? 0);
      if (Number.isFinite(rawSubtotal) && rawSubtotal > 0) {
        return rawSubtotal;
      }

      const total = this.resolveTotal(row);
      const transferred = Number(row?.taxes?.transferred_total ?? 0) || 0;
      const withheld = Number(row?.taxes?.withheld_total ?? 0) || 0;

      return total - transferred + withheld;
    },
    resolveTotal(row) {
      const explicitTotal = Number(row?.total ?? row?.amount ?? 0);
      if (Number.isFinite(explicitTotal)) {
        return explicitTotal;
      }

      return 0;
    },
    resolveTaxItems(row) {
      return Array.isArray(row?.taxes?.items) ? row.taxes.items : [];
    },
    resolveStatusClass(row) {
      return resolveExpenseFeeStatusClass(row);
    },
    isPaid(row) {
      return isExpenseFeePaid(row);
    },
    isPending(row) {
      return isExpenseFeePending(row);
    },
    hasRelatedPayment(row) {
      return hasExpenseFeeRelatedPayment(row);
    },
    isUnapproved(row) {
      return isExpenseFeeUnapproved(row);
    },
    showReferenceActionButton(row) {
      return showExpenseFeeReferenceAction(row);
    },
    canDeleteExpense(row) {
      return canExpenseFeeDelete(row);
    },
    canUnapproveExpense(row) {
      return canExpenseFeeUnapprove(row);
    },
    resolveInlineRowActions(row) {
      return this.resolveRowActions(row).inline;
    },
    resolveMenuRowActions(row) {
      return this.resolveRowActions(row).menu;
    },
    resolveRowActions(row) {
      const inline = this.buildPrimaryRowActions(row);
      const downloads = this.buildDownloadRowActions(row);
      const menu = [];

      while (inline.length > MAX_INLINE_ROW_ACTIONS) {
        const movedAction = inline.pop();
        if (movedAction) {
          menu.unshift(movedAction);
        }
      }

      const availableSlots = Math.max(MAX_INLINE_ROW_ACTIONS - inline.length, 0);
      if (availableSlots > 0) {
        inline.push(...downloads.slice(0, availableSlots));
        menu.push(...downloads.slice(availableSlots));
      } else {
        menu.push(...downloads);
      }

      if (menu.length > 0 && inline.length >= MAX_INLINE_ROW_ACTIONS) {
        const removableInlineActionIndex = this.resolveRemovableInlineActionIndex(inline);
        const fallbackIndex = inline.length - 1;
        const targetIndex = removableInlineActionIndex >= 0 ? removableInlineActionIndex : fallbackIndex;
        const movedAction = inline.splice(targetIndex, 1)[0];
        if (movedAction) {
          menu.unshift(movedAction);
        }
      }

      if (menu.length === 1 && inline.length < MAX_INLINE_ROW_ACTIONS) {
        inline.push(menu.shift());
      }

      return {
        inline,
        menu,
      };
    },
    resolveRemovableInlineActionIndex(inlineActions = []) {
      const preferredKeys = [
        'download-receipt',
        'download-pdf',
        'download-xml',
        'delete',
        'unapprove',
      ];

      for (let index = 0; index < preferredKeys.length; index += 1) {
        const actionKey = preferredKeys[index];
        const actionIndex = inlineActions.findIndex((action) => action?.key === actionKey);
        if (actionIndex >= 0) {
          return actionIndex;
        }
      }

      return -1;
    },
    buildPrimaryRowActions(row) {
      const actions = [
        {
          key: 'edit',
          title: this.labels?.actions?.edit || 'Editar',
          icon: 'fa-pencil',
          buttonClass: 'app-table-icon-btn--edit',
          eventName: 'edit-expense',
          eventArgs: [],
        },
      ];

      if (this.canUnapproveExpense(row)) {
        actions.push({
          key: 'unapprove',
          title: this.labels?.actions?.unapprove || 'No aprobar',
          icon: 'fa-ban',
          buttonClass: 'app-table-icon-btn--deactivate',
          eventName: 'approve-expense',
          eventArgs: [false],
        });
      }

      if (this.canDeleteExpense(row)) {
        actions.push({
          key: 'delete',
          title: this.labels?.actions?.delete || 'Eliminar',
          icon: 'fa-trash',
          buttonClass: 'app-table-icon-btn--delete',
          eventName: 'delete-expense',
          eventArgs: [],
        });
      }

      return actions;
    },
    buildDownloadRowActions(row) {
      const actions = [];

      if (this.hasXmlDocument(row)) {
        actions.push({
          key: 'download-xml',
          title: this.labels?.actions?.downloadXml || 'Descargar XML',
          icon: 'fa-file-code-o',
          buttonClass: '',
          eventName: 'download-xml',
          eventArgs: [],
        });
      }

      if (this.hasPdfDocument(row)) {
        actions.push({
          key: 'download-pdf',
          title: this.labels?.actions?.downloadPdf || 'Descargar PDF',
          icon: 'fa-file-pdf-o',
          buttonClass: '',
          eventName: 'download-pdf',
          eventArgs: [],
        });
      }

      if (this.hasReceiptDocument(row)) {
        actions.push({
          key: 'download-receipt',
          title: this.labels?.actions?.downloadReceipt || 'Descargar comprobante',
          icon: 'fa-file-text-o',
          buttonClass: '',
          eventName: 'download-receipt',
          eventArgs: [],
        });
      }

      return actions;
    },
    hasXmlDocument(row) {
      return this.resolveDocumentAvailability(row, ['has_xml'], ['invoice_xml', 'xml', 'xml_url', 'xml_file']);
    },
    hasPdfDocument(row) {
      return this.resolveDocumentAvailability(row, ['has_pdf'], ['invoice_pdf', 'pdf', 'pdf_url', 'pdf_file']);
    },
    hasReceiptDocument(row) {
      return this.resolveDocumentAvailability(
        row,
        ['has_receipt'],
        ['receipt', 'voucher_file', 'receipt_url', 'voucher_url', 'comprobante']
      );
    },
    resolveDocumentAvailability(row, explicitFlagFields = [], fallbackPathFields = []) {
      const source = row && typeof row === 'object' ? row : {};

      for (let index = 0; index < explicitFlagFields.length; index += 1) {
        const field = explicitFlagFields[index];
        if (Object.prototype.hasOwnProperty.call(source, field)) {
          return toBooleanFlag(source[field]);
        }
      }

      return fallbackPathFields.some((field) => String(source?.[field] || '').trim() !== '');
    },
    runInlineRowAction(action, row) {
      this.emitRowAction(action, row, false);
    },
    runMenuRowAction(action, row) {
      this.emitRowAction(action, row, true);
    },
    emitRowAction(action, row, closeMenu = false) {
      if (closeMenu) {
        this.openActionsMenuId = null;
      }

      const eventName = String(action?.eventName || '').trim();
      if (eventName === '') {
        return;
      }

      const extraArgs = Array.isArray(action?.eventArgs) ? action.eventArgs : [];
      this.$emit(eventName, row, ...extraArgs);
    },
    referenceActionLabel(row) {
      return resolveExpenseFeeReferenceActionLabel(row);
    },
    emitReferenceAction(row) {
      if (this.isUnapproved(row)) {
        this.$emit('approve-expense', row, true);
        return;
      }

      this.$emit('pay-expense', row);
    },
    resolveProviderHref(row) {
      return resolveRouteHref(this.$router, 'supplierDetail', 'supplierId', row?.provider_id);
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.customer_id);
    },
    resolveSaleHref(row) {
      return resolveRouteHref(this.$router, 'saleDetail', 'saleId', row?.sale_id);
    },
    resolveExpenseReceiptId(row) {
      const directPaymentId = Number(row?.payment_id || 0);
      if (directPaymentId > 0) {
        return directPaymentId;
      }

      const nestedPaymentId = Number(row?.payment?.id || 0);
      if (nestedPaymentId > 0) {
        return nestedPaymentId;
      }

      const explicitReceiptId = Number(row?.expense_receipt_id || row?.receipt_id || 0);
      return explicitReceiptId > 0 ? explicitReceiptId : 0;
    },
    canNavigateToExpenseReceipt(row) {
      return this.resolveExpenseReceiptId(row) > 0;
    },
    resolveExpenseReceiptHref(row) {
      return resolveRouteHref(this.$router, 'expenseReceiptDetail', 'expenseReceiptId', this.resolveExpenseReceiptId(row));
    },
    navigateToProviderDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'supplierDetail', 'supplierId', row?.provider_id, event);
    },
    navigateToCustomerDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.customer_id, event);
    },
    navigateToSaleDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'saleDetail', 'saleId', row?.sale_id, event);
    },
    navigateToExpenseReceiptDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'expenseReceiptDetail', 'expenseReceiptId', this.resolveExpenseReceiptId(row), event);
    },
    toggleActionsMenu(rowId, event) {
      const button = event?.currentTarget || null;
      const row = this.normalizedRows.find((item) => Number(item?.id || 0) === Number(rowId)) || null;
      if (!row || this.resolveMenuRowActions(row).length === 0) {
        this.openActionsMenuId = null;
        return;
      }

      this.openActionsMenuId = resolveToggledMenuId(this.openActionsMenuId, rowId);

      if (button && this.openActionsMenuId !== null) {
        this.setFloatingMenuPosition(button);
      }
    },
    setFloatingMenuPosition(buttonElement) {
      const position = computeFloatingMenuPosition(buttonElement, 210);
      this.floatingMenuLeft = position.left;
      this.floatingMenuTop = position.top;
    },
    handleDocumentClick(event) {
      const target = event.target;
      if (target && (target.closest('.app-table-actions-menu-wrap') || target.closest('.app-table-floating-menu'))) {
        return;
      }

      this.openActionsMenuId = null;
    },
  },
};
</script>
